import * as Popover from '@radix-ui/react-popover'
import * as Checkbox from '@radix-ui/react-checkbox'
import { ChevronDownIcon, CheckIcon } from '@heroicons/react/20/solid'
import { forwardRef, useState } from 'react'
import clsx from 'clsx'

export type MultiSelectOption = {
  value: string
  label: string
}

export type MultiSelectProps = {
  options: MultiSelectOption[]
  selected: string[]
  onChange: (selected: string[]) => void
  placeholder?: string
  className?: string
  enableClearAll?: boolean
}

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(function MultiSelect(
  { options, selected, onChange, placeholder = 'Select options...', className, enableClearAll = false },
  ref
) {
  const [open, setOpen] = useState(false)

  const handleToggle = (value: string) => {
    const newSelected = selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value]
    onChange(newSelected)
  }

  const handleClear = () => {
    onChange([])
  }

  const selectedLabels = options.filter((opt) => selected.includes(opt.value)).map((opt) => opt.label)
  const displayText = selectedLabels.length > 0 ? selectedLabels.join(', ') : placeholder

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <Popover.Trigger asChild>
        <button
          ref={ref}
          type="button"
          className={clsx(
            'w-full flex items-center justify-between rounded-[4px]  bg-white px-3 py-2.5 text-left text-sm',
            'border border-1 brand-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-400 hover:outline-none hover:ring-2 hover:ring-brand-400', // hocus doesn't work here
            className
          )}
        >
          <div className="flex flex-col flex-1 min-w-0">
            {placeholder && selectedLabels.length > 0 && <label className="text-xs text-brand-neutral-600">{placeholder}</label>}
            <span className={clsx('block truncate text-base', selectedLabels.length === 0 ? 'text-brand-neutral-600' : 'text-brand-neutral-950')}>
              {displayText}
            </span>
          </div>
          <ChevronDownIcon className={clsx('h-5 w-5 text-brand-neutral-400 transition-transform', open && 'rotate-180')} />
        </button>
      </Popover.Trigger>

      <Popover.Portal>
        <Popover.Content
          className="w-[var(--radix-popover-trigger-width)] rounded-md border border-brand-neutral-300 bg-white shadow-lg z-[1001] max-h-80 overflow-y-auto"
          sideOffset={4}
          align="start"
        >
          {/* Header with clear button */}
          {enableClearAll && selected.length > 0 && (
            <div className="border-b border-brand-neutral-200 px-3 py-2 flex justify-between items-center">
              <span className="text-xs text-brand-neutral-600">{selected.length} selected</span>
              <button type="button" onClick={handleClear} className="text-xs text-brand-500 hocus:text-brand-700 font-medium">
                Clear all
              </button>
            </div>
          )}

          {/* Options */}
          <div className="p-2">
            {options.map((option) => {
              const isSelected = selected.includes(option.value)
              return (
                <label key={option.value} className="flex items-center gap-2.5 p-2 cursor-pointer hover:bg-brand-neutral-100">
                  <Checkbox.Root
                    checked={isSelected}
                    onCheckedChange={() => handleToggle(option.value)}
                    className={clsx(
                      'h-4 w-4 rounded flex items-center justify-center',
                      'hocus:outline-none hocus:ring-2 hocus:ring-brand-500 hocus:ring-offset-2',
                      isSelected ? 'bg-brand-500' : 'border border-brand-neutral-400'
                    )}
                  >
                    <Checkbox.Indicator>
                      <CheckIcon className="h-3 w-3 text-white" />
                    </Checkbox.Indicator>
                  </Checkbox.Root>
                  <span className="text-sm text-brand-neutral-700 flex-1">{option.label}</span>
                </label>
              )
            })}
          </div>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
})
